import { usePassportProvider } from "@/context";
import { useLoginModal } from "@/context/LoginModalContext";
import { Stack } from "@biom3/react";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useWeeklyRewards } from "../WeeklyRewardsProvider";
import type { EligibilityResponse } from "../types";
import { EligibilityItem, type EligibilityItemProps } from "./EligibilityItem";
import { PhoneVerificationModal } from "./PhoneVerificationModal";

const COLLAPSE_AFTER_COMPLETION_TIMEOUT = 2500;

export function createEligibilityCriteria(
  eligibility: EligibilityResponse | undefined,
): EligibilityItemProps[] {
  return [
    {
      id: "have_linked_any_social_media",
      icon: "SocialShare",
      title: "Link a social account",
      description: "Connect at least one social media account to your profile.",
      disabled: false,
      completed: !!eligibility?.rules.have_linked_any_social_media,
    },
    {
      id: "have_verified_phone",
      icon: "ApplicationMobile",
      title: "Verify your phone number",
      description:
        "Add and confirm your phone number for stronger account security.",
      disabled: !eligibility?.rules.have_linked_any_social_media,
      completed: !!eligibility?.rules.have_verified_phone,
    },
  ];
}

export function Eligibility() {
  const { eligibility, mutateEligibility } = useWeeklyRewards();
  const { passportState } = usePassportProvider();
  const { openLoginModal } = useLoginModal();
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const router = useRouter();
  const isCollapsible = true;

  const [isExpanded, setIsExpanded] = useState(true);
  const [activeItemId, setActiveItemId] = useState<
    EligibilityItemProps["id"] | undefined
  >(undefined);

  const handleClick = useCallback(
    (criteria: EligibilityItemProps) => {
      if (criteria.disabled || criteria.completed) {
        return;
      }

      setActiveItemId(criteria.id);

      if (!eligibility && passportState.ready && !passportState.authenticated) {
        openLoginModal();
        return;
      }

      switch (criteria.id) {
        case "have_linked_any_social_media":
          router.push("/settings?tab=social");
          break;
        case "have_verified_phone":
          setIsPhoneModalOpen(true);
          break;
        default:
          break;
      }
    },
    [
      eligibility,
      passportState.authenticated,
      passportState.ready,
      openLoginModal,
      router,
    ],
  );

  const eligibilityCriteria: EligibilityItemProps[] = useMemo(
    () => createEligibilityCriteria(eligibility),
    [eligibility],
  );

  useEffect(() => {
    // if activeItemId is undefined, find first item that is not completed
    // if the active item is completed, find the next item that is not completed
    const activeItem = activeItemId
      ? eligibilityCriteria.find((item) => item.id === activeItemId)
      : undefined;

    if (!activeItemId || activeItem?.completed) {
      const firstIncompleteItem = eligibilityCriteria.find(
        (item) => !item.completed,
      );
      setActiveItemId(firstIncompleteItem?.id);
    }

    // if all are completed, collapse accordion (only if collapsible)
    if (isCollapsible && eligibilityCriteria.every((item) => item.completed)) {
      const timeoutId = setTimeout(() => {
        setIsExpanded(false);
      }, COLLAPSE_AFTER_COMPLETION_TIMEOUT);

      // Clean up the timeout when the component unmounts or dependencies change
      return () => clearTimeout(timeoutId);
    }
  }, [eligibilityCriteria, activeItemId]);

  return (
    <Stack sx={{ maxWidth: "850px", marginBottom: "base.spacing.x4" }}>
      {eligibilityCriteria.map((criteria) => (
        <EligibilityItem
          key={criteria.title}
          active={activeItemId === criteria.id}
          onClick={() => handleClick(criteria)}
          {...criteria}
        />
      ))}

      <PhoneVerificationModal
        visible={isPhoneModalOpen}
        onClose={() => setIsPhoneModalOpen(false)}
        onVerified={() => {
          mutateEligibility();
          setIsPhoneModalOpen(false);
        }}
      />
    </Stack>
  );
}
